import type { HeroBannerImageData, HeroBannerResponse } from '~/types/lexas/heroBanner.ts'
import type { TileBannerImageData, TileBannerResponse } from '~/types/lexas/tileBanner.ts'
import type { BrandImage, BrandListResponse } from '~/types/lexas/brandList.ts'
import type { PopularCategoriesResponse, PopularCategoryImage } from '~/types/lexas/popularCategories.ts'
import type { StickyResponse } from '~/types/lexas/globalMessage.ts'
import type { PageMessageResponse } from '~/types/lexas/ribbon.ts'
import type { ProductTileResponse } from '~/types/lexas/productTileBanner.ts'
import type { CmsPageResponse } from '~/types/lexas/cmsPage'
import type { BlogPostResponse, CombinedBlogPost } from '~/types/lexas/blogPosts.ts'
import type { ContentBlockResponse } from '~/types/lexas/contentBlock.ts'

/**
 * LexasCMS content through the cached server route /api/cms (server/api/cms/[...path].get.ts).
 */
export function useLexas() {
  // Fetch hero banners based on path
  const getHeroBannerListByPath = async (path: string) => {
    try {
      // Convert homepage path to 'homepage'
      const bannerPath = path === '/' ? 'homepage' : path
      const data: HeroBannerResponse = await $fetch<HeroBannerResponse>(
        `/api/cms/heroBannerList?filter[path][_eq]=${bannerPath}&page[limit]=1&include=banners.componentHeroBannerMobileImage,banners.componentHeroBannerDesktopImage`
      )
      // Check if data is empty or missing, return an empty array if so
      if (!data || !data.included || data.included.length === 0) {
        return []
      }
      // Filter hero banners from the data
      // @ts-ignore
      const banners = data.included.filter(item => item.type === 'componentHeroBanner')
      // Filter images from the data
      // @ts-ignore
      const images = data.included.filter(item => item.type === 'coreImage')

      // Find an image by ID and returns attributes
      const findImageById = (imageId: string): HeroBannerImageData['attributes'] | null => {
        return images.find((item: HeroBannerImageData) => item.id === imageId)?.attributes || null
      }

      // Return a list of hero banners including mobile images, desktop images and links
      return banners.map((banner: any) => ({
        mobileImage: findImageById(banner.relationships.componentHeroBannerMobileImage.data.id),
        desktopImage: findImageById(banner.relationships.componentHeroBannerDesktopImage.data.id),
        link: banner.attributes.componentHeroBannerLink
      }))
    }
    catch (error) {
      console.error('Error:', error)
      return []
    }
  }

  const getTileBannerListByPath = async (path: string) => {
    try {
      // Convert homepage path to 'homepage'
      const bannerPath = path === '/' ? 'homepage' : path
      const data: TileBannerResponse = await $fetch<TileBannerResponse>(
        `/api/cms/tileBannerList?filter[path][_eq]=${bannerPath}&page[limit]=1&include=banners,banners.componentTileBannerMobileImage,banners.componentTileBannerDesktopImage`
      )

      // Check if data is empty or missing, return an empty array if so
      if (!data || !data.included || data.included.length === 0) {
        return []
      }
      // Filter tile banners from the data
      // @ts-ignore
      const banners = data.included.filter(item => item.type === 'componentTileBanner')

      // Filter images from the data
      // @ts-ignore
      const images = data.included.filter(item => item.type === 'coreImage')

      // Find an image by ID and returns attributes
      const findImageById = (imageId: string): TileBannerImageData['attributes'] | null => {
        return images.find((item: TileBannerImageData) => item.id === imageId)?.attributes || null
      }

      // Return list of banners including mobile and desktop images and links
      return banners.map((banner: any) => ({
        mobileImage: findImageById(banner.relationships.componentTileBannerMobileImage.data.id),
        desktopImage: findImageById(banner.relationships.componentTileBannerDesktopImage.data.id),
        link: banner.attributes.componentTileBannerLink
      }))
    }
    catch (error) {
      console.error('Error:', error)
      return []
    }
  }

  const getCategoryBannerListByPath = async (path: string) => {
    try {
      // Convert homepage path to 'homepage'
      const bannerPath = path === '/' ? 'homepage' : path
      const data: HeroBannerResponse = await $fetch<HeroBannerResponse>(
        `/api/cms/categoryBannerList?filter[path][_eq]=${bannerPath}&page[limit]=1&include=banners.componentCategoryBannerMobileImage,banners.componentCategoryBannerDesktopImage`
      )

      if (!data || !data.included || data.included.length === 0) {
        return []
      }
      // @ts-ignore
      const banners = data.included.filter(item => item.type === 'componentCategoryBanner')
      // @ts-ignore
      const images = data.included.filter(item => item.type === 'coreImage')

      const findImageById = (imageId: string): HeroBannerImageData['attributes'] | null => {
        return images.find((item: HeroBannerImageData) => item.id === imageId)?.attributes || null
      }

      return banners.map((banner: any) => ({
        mobileImage: findImageById(banner.relationships.componentCategoryBannerMobileImage.data.id),
        desktopImage: findImageById(banner.relationships.componentCategoryBannerDesktopImage.data.id),
        link: banner.attributes.componentCategoryBannerLink
      }))
    }
    catch (error) {
      console.error('Error:', error)
      return []
    }
  }

  const getBrandList = async () => {
    try {
      // Convert homepage path to 'homepage'
      const data: BrandListResponse = await $fetch<BrandListResponse>(
        `/api/cms/brandList?page[limit]=1&include=brands,brands.componentBrandLogo`
      )
      // Check if data is empty or missing, return an empty array if so
      if (!data || !data.included || data.included.length === 0) {
        return []
      }
      // Filter brand banners from the data
      const banners = data.included.filter(item => item.type === 'componentBrand')

      // Filter images from the data
      const images = data.included.filter(item => item.type === 'coreImage')

      // Find an image by ID and return attributes
      const findImageById = (imageId: string): BrandImage['attributes'] | null => {
        return images.find((item: BrandImage) => item.id === imageId)?.attributes || null
      }

      // Return list of banners including images and links
      return banners.map((banner: any) => ({
        image: findImageById(banner.relationships.componentBrandLogo.data.id),
        link: banner.attributes.componentBrandLink
      }))
    }
    catch (error) {
      console.error('Error:', error)
      return []
    }
  }

  const getCategoryProductTile = async () => {
    try {
      const data: ProductTileResponse = await $fetch<ProductTileResponse>(
        `/api/cms/productListBanner?page[limit]=1&include=productListBannerMobileImage`
      )

      if (!data || !data.included || data.included.length === 0) {
        console.warn('Error')
        return {
          mobileImageData: null
        }
      }

      const mobileImageData = data.included[0].attributes
      const linkData = data.data[0].attributes.productListBannerLink

      return {
        mobileImageData: {
          ...mobileImageData,
          linkData
        }
      }
    }
    catch (error) {
      console.error('Error:', error)

      return {
        mobileImageData: null
      }
    }
  }

  const getCmsPage = (path: string) => {
    // Convert homepage path to 'homepage'
    const cmsPath = path === '/' ? 'homepage' : path
    return useFetch<CmsPageResponse>(
      `/api/cms/sisaltosivu?filter[sisaltosivunUrlPolku][_eq]=${cmsPath}&include=sisltsivunKuva`
    )
  }

  const getContentBlock = (id: string) => {
    return useFetch<ContentBlockResponse>(
      `/api/cms/contentblock?filter[identifier][_eq]=${id}&page[limit]=1`,
      {
        transform: (res: any) => res.data[0].attributes.content
      }
    )
  }

  const getCmsChildPages = (path: string) => {
    // Convert homepage path to 'homepage'
    const cmsPath = path === '/' ? 'homepage' : path
    return useFetch<CmsPageResponse>(
      `/api/cms/sisaltosivu?filter[sisaltosivunUrlPolku][_startsWith]=${cmsPath}`
    )
  }
  // 100 is maximum you can fetch from LexasCMS. With "limit" you can restricts the number of fetch results.
  // getBlogPosts accepts numbers 1-100 to restrict the number of results.
  const getBlogPosts = (limit: number = 100) => {
    return useFetch<CombinedBlogPost[]>(
      `/api/cms/blogiArtikkeli?include=blogiartikkelinKuva1&page[limit]=${limit}&sort=-blogiartikkelinJulkaisupaiva`,
      {
        transform: (data: any) => {
          const combinedData = data.data.map((item: any) => {
            const includedItem = data.included?.find(
              (incItem: any) => incItem.id === item.relationships.blogiartikkelinKuva1?.data?.id
            )
            return {
              ...item.attributes,
              assetId: includedItem?.attributes.assetId || null,
              altText: includedItem?.attributes.altText || null,
              url: includedItem?.attributes.url || null
            }
          })
          return combinedData
        }
      }
    )
  }

  const getBlogPost = (slug: string) => {
    return useFetch<BlogPostResponse>(
      `/api/cms/blogiArtikkeli?filter[blogiartikkelinSlug][_eq]=${slug}&include=blogiartikkelinKuva1`
    )
  }

  const getGlobalMessageData = () => {
    return useFetch(
      `/api/cms/sticky`,
      {
        transform: (data: StickyResponse) => {
          const item = data.data[0]
          return {
            text: item.attributes.text,
            link: item.attributes.link
          }
        }
      }
    )
  }

  const getPageMessageData = () => {
    return useFetch(
      `/api/cms/ribbon`,
      {
        transform: (data: PageMessageResponse) => {
          const item = data.data[0]
          return {
            textLeft: item.attributes.textLeft,
            linkLeft: item.attributes.linkLeft,
            textRight: item.attributes.textRight,
            linkRight: item.attributes.linkRight,
            contentLeft: item.attributes.contentLeft,
            contentRight: item.attributes.contentRight
          }
        }
      }
    )
  }

  const getPopularCategories = async () => {
    try {
      // Convert homepage path to 'homepage'
      const data: PopularCategoriesResponse = await $fetch<PopularCategoriesResponse>(
        `/api/cms/popularCategories?page[limit]=1&include=popularCategories,popularCategories.componentPopularCategoryImage`
      )
      // Check if data is empty or missing, return an empty array if so
      if (!data || !data.included || data.included.length === 0) {
        return []
      }
      // Filter category data from the data
      const categories = data.included.filter(item => item.type === 'componentPopularCategory')

      // Filter images from the data
      const images = data.included.filter(item => item.type === 'coreImage')

      // Find an image by ID and return attributes
      const findImageById = (imageId: string): PopularCategoryImage['attributes'] | null => {
        return images.find((item: PopularCategoryImage) => item.id === imageId)?.attributes || null
      }

      // Return list of categories including name, path and image
      return categories.map((category: any) => ({
        name: category.attributes.componentPopularCategoryName,
        path: category.attributes.componentPopularCategoryPath,
        image: findImageById(category.relationships.componentPopularCategoryImage.data.id)
      }))
    }
    catch (error) {
      console.error('Error:', error)
      return []
    }
  }

  // Fetch the extra homepage banner ("etusivunlisabanneri")
  const getHomePageBanner = async () => {
    try {
      const data: any = await $fetch(
        `/api/cms/etusivunlisabanneri?page[limit]=1&include=banneri,mobiilibanneri`
      )

      // Check if data is empty or missing, return null if so
      if (!data || !data.data || data.data.length === 0) {
        return null
      }

      // Take the single banner row
      const banner = data.data[0]

      // Filter images from the data
      // @ts-ignore
      const images = data.included?.filter(item => item.type === 'coreImage') || []

      // Find an image by ID and return attributes
      const findImageById = (imageId: string | undefined): any => {
        return images.find((item: any) => item.id === imageId)?.attributes || null
      }

      // Return the banner including desktop image, mobile image and link
      return {
        desktopImage: findImageById(banner.relationships?.banneri?.data?.id),
        mobileImage: findImageById(banner.relationships?.mobiilibanneri?.data?.id),
        link: banner.attributes?.linkki || ''
      }
    }
    catch (error) {
      console.error('Error:', error)
      return null
    }
  }

  return {
    getHeroBannerListByPath,
    getTileBannerListByPath,
    getCategoryBannerListByPath,
    getBrandList,
    getCategoryProductTile,
    getCmsPage,
    getCmsChildPages,
    getBlogPosts,
    getBlogPost,
    getGlobalMessageData,
    getContentBlock,
    getPageMessageData,
    getPopularCategories,
    getHomePageBanner
  }
}
